import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import AppCtaLink from '../components/AppCtaLink';
import CrisisSupport from '../components/CrisisSupport';
import FAQ from '../components/FAQ';
import HomeCompare from '../components/home/HomeCompare';
import HomeLimits from '../components/home/HomeLimits';
import LumiMerchStrip from '../components/home/LumiMerchStrip';
import LumiStoriesCarousel from '../components/home/LumiStoriesCarousel';
import {
  COMPANY_NUMBER,
  COMPANIES_HOUSE_URL,
  CONTACT_EMAIL,
  LEGAL_NAME,
  LEGAL_NAME_REGISTERED,
  NGO_EDRPOU,
  REGISTERED_ADDRESS
} from '../data/legalEntity';
import { RELATED_LABELS, TOPIC_PAGES, worldSrc } from '../data/lumiTopics';
import { LUMI_YOUTUBE } from '../data/lumiStories';

const LumiTopicPage = ({ slug }) => {
  const { t } = useTranslation();
  const { language } = useParams();
  const lng = (language || 'en').split('-')[0];
  const page = TOPIC_PAGES[slug] || TOPIC_PAGES['how-lumi-works'];
  const base = `lumi_pages.${slug}`;
  const title = t(`${base}.title`);
  const description = t(`${base}.description`);
  const h1 = t(`${base}.h1`);
  const intro = t(`${base}.intro`);
  const paragraphs = t(`${base}.paragraphs`, { returnObjects: true });
  const paras = Array.isArray(paragraphs) ? paragraphs : [];
  const placement = `lumi_${slug.replace(/-/g, '_')}`;
  const lean = page.kind === 'impressum' || page.kind === 'faq' || page.kind === 'topic';

  const faqJson = useMemo(() => {
    if (page.kind !== 'faq') return null;
    const faqItems = t('faq.questions', { returnObjects: true, defaultValue: [] });
    if (!Array.isArray(faqItems) || !faqItems.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: String(item.answer || '').replace(/\n/g, ' ') }
      }))
    };
  }, [page.kind, t]);

  if (lean) {
    return (
      <article className="lumi-page">
        <SEO title={title} description={description} image="/assets/lumi/brand/lumi-mascot.png" jsonLd={faqJson || undefined} />
        <div className="container lumi-doc">
          {page.kind === 'impressum' ? (
            <>
              <h1>Impressum</h1>
              <div className="lumi-facts">
                <p>{LEGAL_NAME_REGISTERED}</p>
                <p>{LEGAL_NAME}</p>
                <p>{REGISTERED_ADDRESS}</p>
                <p>
                  Company number {COMPANY_NUMBER}.{' '}
                  <a href={COMPANIES_HOUSE_URL} target="_blank" rel="noopener noreferrer">Companies House</a>
                </p>
                <p><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
                <p>Громадська організація «ПСАЙХЕЛП» · ЄДРПОУ {NGO_EDRPOU}</p>
              </div>
            </>
          ) : (
            <>
              <h1>{h1}</h1>
              {page.kind !== 'faq' ? <p className="lumi-page__lead">{intro}</p> : null}
              {page.images[0] ? (
                <img
                  className="lumi-doc__image"
                  src={worldSrc(page.images[0], lng)}
                  alt={t(`${base}.image_alt`, { defaultValue: h1 })}
                  width="640"
                  height="640"
                  loading="lazy"
                  decoding="async"
                />
              ) : null}
              {page.kind === 'topic' ? paras.map((p) => <p key={p.slice(0, 32)}>{p}</p>) : null}
              {page.kind === 'faq' ? <FAQ bare /> : null}
              {page.kind === 'topic' ? (
                <div className="lumi-hero__actions lumi-doc__stores">
                  <AppCtaLink platform="web" placement={placement} className="lumi-hero__web">
                    <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
                      <path fill="currentColor" d="M12 2a10 10 0 1 0 .01 20.01A10 10 0 0 0 12 2zm6.9 9h-3.18a15.7 15.7 0 0 0-1.2-5.02A8.03 8.03 0 0 1 18.9 11zM12 4c.9 1.16 1.7 3.05 2.05 5H9.95C10.3 7.05 11.1 5.16 12 4zM4.26 13h3.36a17.2 17.2 0 0 0 0-2H4.26a8.06 8.06 0 0 0 0 2zm.84 2h3.18a15.7 15.7 0 0 0 1.2 5.02A8.03 8.03 0 0 1 5.1 15zm3.18-6H5.1a8.03 8.03 0 0 1 4.38-5.02A15.7 15.7 0 0 0 8.28 9zM12 20c-.9-1.16-1.7-3.05-2.05-5h4.1c-.35 1.95-1.15 3.84-2.05 5zm2.34-7H9.66a15.4 15.4 0 0 1 0-2h4.68a15.4 15.4 0 0 1 0 2zm.18 6.02A15.7 15.7 0 0 0 15.72 15h3.18a8.03 8.03 0 0 1-4.38 5.02zM19.74 13a8.06 8.06 0 0 0 0-2h-3.36a17.2 17.2 0 0 0 0 2h3.36z" />
                    </svg>
                    <span>
                      <small>Web</small>
                      {t('home.cta')}
                    </span>
                  </AppCtaLink>
                  <AppCtaLink platform="android" placement={placement} className="lumi-hero__store" aria-label={t('home.value_section.apps.google')}>
                    <img src="/images/google_en.svg" alt="" width="135" height="40" decoding="async" />
                  </AppCtaLink>
                  <AppCtaLink platform="ios" placement={placement} className="lumi-hero__store" aria-label={t('home.value_section.apps.apple')}>
                    <img src="/images/apple_en.svg" alt="" width="135" height="40" decoding="async" />
                  </AppCtaLink>
                </div>
              ) : null}
            </>
          )}
          <CrisisSupport language={lng} />
        </div>
      </article>
    );
  }

  return (
    <article className="lumi-page">
      <SEO
        title={title}
        description={description}
        image="/assets/lumi/brand/lumi-mascot.png"
        jsonLd={faqJson || undefined}
      />
      <div className="container">
        <p className="lumi-page__brand">{t('lumi.hero.brand_line')}</p>
        <h1>{h1}</h1>
        <p className="lumi-page__lead">{intro}</p>

        {page.kind !== 'impressum' && page.kind !== 'faq' ? (
          <img
            className="lumi-page__mascot"
            src="/assets/lumi/brand/lumi-mascot.webp"
            alt={t('lumi.hero.mascot_alt')}
            width="160"
            height="160"
            loading="lazy"
            decoding="async"
          />
        ) : null}

        {page.images.length ? (
          <div className="lumi-page__gallery">
            {page.images.map((name) => (
              <img
                key={name}
                src={worldSrc(name, lng)}
                alt={t(`${base}.image_alt`, { defaultValue: h1 })}
                width="600"
                height="600"
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        ) : null}

        {paras.map((p) => (
          <p key={p.slice(0, 24)}>{p}</p>
        ))}

        {page.kind === 'impressum' ? (
          <section>
            <h2>{LEGAL_NAME_REGISTERED}</h2>
            <p>{LEGAL_NAME}</p>
            <p>{REGISTERED_ADDRESS}</p>
            <p>
              Company number {COMPANY_NUMBER}.{' '}
              <a href={COMPANIES_HOUSE_URL} target="_blank" rel="noopener noreferrer">Companies House</a>
            </p>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
            <p>[TODO Andrew: responsible person for German-language content]</p>
            <p>[TODO Andrew: VAT / USt-IdNr. if registered]</p>
            <p>
              EU online dispute resolution:{' '}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
                https://ec.europa.eu/consumers/odr
              </a>
            </p>
          </section>
        ) : null}

        {page.kind === 'how' ? (
          <>
            <HomeCompare />
            <HomeLimits />
          </>
        ) : null}

        {page.kind === 'faq' ? <FAQ /> : null}
        {page.kind === 'stories' ? (
          <>
            <LumiStoriesCarousel />
            <p>
              <a href={LUMI_YOUTUBE} target="_blank" rel="noopener noreferrer">YouTube @AIPSYHELP</a>
            </p>
          </>
        ) : null}

        {page.kind === 'download' ? (
          <div className="hero-app-stores">
            <AppCtaLink platform="android" placement="lumi_download" className="hero-app-store-link">
              <img src="/images/google_en.svg" alt={t('home.value_section.apps.google')} width="155" height="46" />
            </AppCtaLink>
            <AppCtaLink platform="ios" placement="lumi_download" className="hero-app-store-link">
              <img src="/images/apple_en.svg" alt={t('home.value_section.apps.apple')} width="155" height="46" />
            </AppCtaLink>
          </div>
        ) : null}

        <div className="hero-cta-row" style={{ marginTop: 24 }}>
          <AppCtaLink platform="web" placement={`lumi_${slug.replace(/-/g, '_')}`} className="btn btn-primary">
            {t('home.cta')}
          </AppCtaLink>
          <AppCtaLink platform="android" placement={`lumi_${slug.replace(/-/g, '_')}`} className="btn btn-ghost">
            {t('home.value_section.apps.google')}
          </AppCtaLink>
          <AppCtaLink platform="ios" placement={`lumi_${slug.replace(/-/g, '_')}`} className="btn btn-ghost">
            {t('home.value_section.apps.apple')}
          </AppCtaLink>
        </div>

        <CrisisSupport language={lng} />
        <p className="lumi-page__note">{t('lumi_pages.not_diagnosis')}</p>

        <h2>{t('lumi_pages.related_title')}</h2>
        <div className="lumi-page__related">
          {page.related.map((rel) => (
            <Link key={rel} to={`/${lng}/${rel}`}>
              {t(RELATED_LABELS[rel] || rel)}
            </Link>
          ))}
          {page.news.map((id) => (
            <Link key={id} to={`/${lng}/news/${id}`}>
              {t(`news.news_items.${id}.title`, { defaultValue: t('nav.news') })}
            </Link>
          ))}
        </div>
        <p className="lumi-page__brand">{t('footer.brand_line')}</p>
      </div>
      {page.kind === 'stories' ? <LumiMerchStrip /> : null}
    </article>
  );
};

export default LumiTopicPage;
